import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectorRef,
  ComponentFactoryResolver,
  Directive,
  ElementRef,
  inject,
  Injector,
  output,
  ViewContainerRef,
  input,
} from '@angular/core';

import { ColorPickerComponent } from './color-picker.component';
import { ColorPickerService } from './color-picker.service';

type ColorPickerChange = { slider: string; value: { v: number; rg: number } };

@Directive({
  selector: '[rtColorPicker]',
  host: {
    '(input)': 'changeInput($any($event.target).value)',
    '(click)': 'onClick()',
  },
  standalone: true,
})
export class ColorPickerDirective implements OnInit, OnChanges {
  private readonly vcRef = inject(ViewContainerRef);
  private readonly el = inject(ElementRef);
  private readonly service = inject(ColorPickerService);
  private readonly cfr = inject(ComponentFactoryResolver);
  private readonly cdRef = inject(ChangeDetectorRef);
  public readonly colorPicker = input<string>(undefined!, { alias: 'rtColorPicker' });
  public readonly cpToggle = input<boolean>(undefined!);
  public readonly cpPosition = input<string>('right');
  public readonly cpPositionOffset = input<string>('0%');
  public readonly cpPositionRelativeToArrow = input<boolean>(false);
  public readonly cpOutputFormat = input<string>('hex');
  public readonly cpPresetLabel = input<string>('Preset colors');
  public readonly cpPresetColors = input<string[]>(undefined!);
  public readonly cpCancelButton = input<boolean>(false);
  public readonly cpCancelButtonClass = input<string>('cp-cancel-button-class');
  public readonly cpCancelButtonText = input<string>('Cancel');
  public readonly cpOKButton = input<boolean>(false);
  public readonly cpOKButtonClass = input<string>('cp-ok-button-class');
  public readonly cpOKButtonText = input<string>('OK');
  public readonly cpFallbackColor = input<string>('#ffffff');
  public readonly cpHeight = input<string>('auto');
  public readonly cpWidth = input<string>('230px');
  public readonly cpIgnoredElements = input<any[]>([]);
  public readonly cpDialogDisplay = input<string>('popup');
  public readonly cpSaveClickOutside = input<boolean>(true);
  public readonly cpAlphaChannel = input<string>('hex6');

  public colorPickerSelect = output<string>();
  public colorPickerChange = output<string>();
  public cpInputChange = output<ColorPickerChange>();
  public cpSliderChange = output<ColorPickerChange>();
  public cpToggleChange = output<boolean>();

  private dialog: ColorPickerComponent;
  private created: boolean;
  private ignoreChanges: boolean = false;

  constructor() {
    this.created = false;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.cpToggle) {
      if (changes.cpToggle.currentValue) this.openDialog();
      if (!changes.cpToggle.currentValue && this.dialog) this.dialog.closeColorPicker();
    }
    if (changes.colorPicker) {
      if (this.dialog && !this.ignoreChanges) {
        if (this.cpDialogDisplay() === 'inline') {
          this.dialog.setInitialColor(changes.colorPicker.currentValue);
        }
        this.dialog.setColorFromString(changes.colorPicker.currentValue, false);
      }
      this.ignoreChanges = false;
    }
    if (changes.cpPresetLabel || changes.cpPresetColors) {
      if (this.dialog) {
        this.dialog.setPresetConfig(this.cpPresetLabel(), this.cpPresetColors());
      }
    }
  }

  public ngOnInit(): void {
    let hsva = this.service.stringToHsva(this.colorPicker());
    if (hsva === null) hsva = this.service.stringToHsva(this.colorPicker(), true);
    if (hsva == null) {
      hsva = this.service.stringToHsva(this.cpFallbackColor());
    }
    const color = this.service.outputFormat(
      hsva,
      this.cpOutputFormat(),
      this.cpAlphaChannel() === 'hex8',
    );
    if (color !== this.colorPicker()) {
      // setTimeout(() => {
      this.colorPickerChange.emit(color);
      this.cdRef.detectChanges();
      // }, 0);
    }
  }

  public onClick(): void {
    if (this.cpIgnoredElements().filter(item => item === this.el.nativeElement).length === 0) {
      this.openDialog();
      this.cdRef.detectChanges();
    }
  }

  public colorChanged(value: string, ignore: boolean = true): void {
    this.ignoreChanges = ignore;
    this.colorPickerChange.emit(value);
  }

  public colorSelected(value: string): void {
    this.colorPickerSelect.emit(value);
  }

  public inputChanged(event: ColorPickerChange): void {
    this.cpInputChange.emit(event);
  }

  public sliderChanged(event: ColorPickerChange): void {
    this.cpSliderChange.emit(event);
  }

  public changeInput(value: string): void {
    this.dialog.setColorFromString(value, true);
  }

  public toggle(value: boolean): void {
    this.cpToggleChange.emit(value);
  }

  private openDialog(): void {
    if (!this.created) {
      this.created = true;
      const injector = Injector.create({ providers: [], parent: this.vcRef.parentInjector });

      const cmpRef = this.vcRef.createComponent(ColorPickerComponent, {
        injector,
        index: 0,
      });
      cmpRef.instance.setDialog(
        this,
        this.el,
        this.colorPicker(),
        this.cpPosition(),
        this.cpPositionOffset(),
        this.cpOutputFormat(),
        this.cpPresetLabel(),
        this.cpPresetColors(),
        this.cpCancelButton(),
        this.cpCancelButtonClass(),
        this.cpCancelButtonText(),
        this.cpOKButton(),
        this.cpOKButtonClass(),
        this.cpOKButtonText(),
        this.cpHeight(),
        this.cpWidth(),
        this.cpIgnoredElements(),
        this.cpDialogDisplay(),
        this.cpSaveClickOutside(),
        this.cpAlphaChannel(),
      );
      this.dialog = cmpRef.instance;
    } else if (this.dialog) {
      this.dialog.openDialog(this.colorPicker());
    }
  }
}
